import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { t, getCurrentLanguage } from '../utils/translations';
import api from './api';
import { CardLoader } from './Loader';

const ProjectsSection = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleLanguageChange = () => setCurrentLanguage(getCurrentLanguage());
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/projects/');
        if (response.data && response.data.length > 0) {
          setProjects(response.data.slice(0, 4));
          setError(null);
        } else {
          setError('Aucun projet disponible');
        }
      } catch (err) {
        setError('Erreur de chargement');
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const translateTitle = (project) => {
    if (currentLanguage === 'en' && project.title_en) return project.title_en;
    return project.title || project.title_fr || '';
  };

  const translateDescription = (project) => {
    if (currentLanguage === 'en' && project.description_en) return project.description_en;
    return project.description || project.description_fr || '';
  };

  if (loading) {
    return (
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <CardLoader /><CardLoader /><CardLoader /><CardLoader />
          </div>
        </div>
      </section>
    );
  }

  if (error || projects.length === 0) return null;

  return (
    <section className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4">

        {/* Section Title - BAD style */}
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('projects.title')}
          </h2>
          <div className="w-16 h-0.5 bg-[#089556] mx-auto"></div>
        </div>

        {/* Cards Grid - adapts to number of projects */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
          projects.length >= 4 ? 'lg:grid-cols-4' : projects.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
        }`}>
          {projects.map((project) => {
            const title = translateTitle(project);
            const description = translateDescription(project);

            return (
              <div key={project.id} className="flex flex-col group">
                {/* Image */}
                <div className="w-full aspect-[4/3] overflow-hidden mb-4 bg-gray-100">
                  <img
                    src={project.image_url || project.image}
                    alt={title}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = '/projets/projet1.jpg'; }}
                  />
                </div>

                {/* Title */}
                <h3 className="text-base font-semibold text-gray-800 mb-2 leading-snug line-clamp-2">
                  {title}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 mb-3 leading-relaxed line-clamp-2 flex-1">
                  {description}
                </p>

                {/* VOIR PLUS link - BAD style */}
                <Link
                  to={`/${currentLanguage}/projet/${project.slug || project.id}`}
                  className="inline-flex items-center text-[#089556] hover:text-[#0a7c4a] text-sm font-semibold uppercase tracking-wide group/link"
                >
                  {currentLanguage === 'fr' ? 'Voir plus' : 'See more'}
                  <svg className="w-3.5 h-3.5 ml-1.5 group-hover/link:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default ProjectsSection;
